const selectors = {
  splideList: '.js-splide-list',
  gallerySlide: '.js-gallery-slide',
  gallerySlideActive: '.js-gallery-slide:not(.splide__slide--clone)',
  thumbnailsCarouselPrevButton: '.js-thumbnails-carousel-prev-button',
  thumbnailsCarouselNextButton: '.js-thumbnails-carousel-next-button'
};

if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class productGallery extends HTMLElement {
      constructor() {
        super()
        this.variants = this.getAttribute('data-variants') || ''
        this.mainCarousel = null
        this.thumbnailCarousel = null
        this.nextArrow = null
        this.prevArrow = null

        const mediaCount = Number.parseInt(this.getAttribute('data-media-count') || '0', 10)
        this.count = Number.isNaN(mediaCount) ? 0 : mediaCount

        this.mountCarousel()
      }
      connectedCallback() {
        this.moveToPrevThumbSlide = this.moveToPrevThumbSlide.bind(this)
        this.moveToNextThumbSlide = this.moveToNextThumbSlide.bind(this)

        this.thumbnailsCarouselPrevButtonEl = this.querySelector(selectors.thumbnailsCarouselPrevButton)
        this.thumbnailsCarouselNextButtonEl = this.querySelector(selectors.thumbnailsCarouselNextButton)

        this.splideListEl = this.querySelector(selectors.splideList)

        if (this.thumbnailsCarouselPrevButtonEl) {
          this.thumbnailsCarouselPrevButtonEl.addEventListener('click', this.moveToPrevThumbSlide)
        }

        if (this.thumbnailsCarouselNextButtonEl) {
          this.thumbnailsCarouselNextButtonEl.addEventListener('click', this.moveToNextThumbSlide)
        }

        if (this.splideListEl) {
          this.originalFirstSlide = this.splideListEl.firstElementChild
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event?.data) return;
          this.updateMedia(event.data)
        })
      }
      disconnectedCallback() {
        if (this.thumbnailsCarouselPrevButtonEl) {
          this.thumbnailsCarouselPrevButtonEl.removeEventListener('click', this.moveToPrevThumbSlide)
        }

        if (this.thumbnailsCarouselNextButtonEl) {
          this.thumbnailsCarouselNextButtonEl.removeEventListener('click', this.moveToNextThumbSlide)
        }

        this.variantChangeUnsubscriber?.()
      }
      moveToNextThumbSlide() {
        this.thumbnailCarousel?.go('>')
      }
      moveToPrevThumbSlide() {
        this.thumbnailCarousel?.go('<')
      }
      isDefault() {
        return !this.variants || this.variants === 'default'
      }
      isActive() {
        return this.variants === 'active'
      }
      updateMedia({ sectionId, variant }) {
        if (!variant || !sectionId || this.dataset.splideId !== sectionId) return

        const slides = [...this.querySelectorAll(selectors.gallerySlideActive)]
        if (!slides.length) return

        const isDestroyed = this.mainCarousel.state.is(Splide.STATES.DESTROYED)

        if (this.isActive() && isDestroyed) {
          const mediaId = variant.featured_media ? buildMediaId(sectionId, variant) : null
          const slide = mediaId ? slides.find((slide) => slide.dataset.mediaId === mediaId) : this.originalFirstSlide

          if (slide && slide !== this.splideListEl.firstElementChild) this.splideListEl.insertBefore(slide, this.splideListEl.firstElementChild)
          return
        }

        const targetIndex = getVariantSlideIndex(slides, sectionId, variant)
        this.mainCarousel?.go(targetIndex)
      }
      getMainCarouselOptions() {
        let options = {
          type: 'loop',
          arrows: true,
          pagination: false,
          perPage: 1,
          perMove: 1,
          gap: 20,
          easing: 'ease',
          width: '67vw',
          lazyLoad: 'nearby',
          breakpoints: {
            1070: {
              width: '100%',
              gap: 0,
              focus: 'center',
              type: 'loop',
              dragMinThreshold: 10,
              flickPower: 0,
              flickMaxPages: 1,
              perPage: 1,
              perMove: 1,
              drag: true,
              snap: true,
              pagination: true
            }
          }
        }

        if (this.isActive()) {
          options = {
            ...options,
            mediaQuery: 'min',
            width: '100%',
            focus: 'center',
            gap: '8px',
            padding: {
              left: '16px',
              right: '16px'
            },
            pagination: true,
            breakpoints: {
              750: {
                destroy: true
              }
            }
          }
        }

        return options
      }
      mountCarousel() {
        const mainOptions = this.getMainCarouselOptions()
        this.mainCarousel = new Splide('#main-slider', mainOptions)

        if (this.isDefault()) {
          var height, focus

          if (this.count < 5) {
            height = 'auto'
            focus = 1
          } else {
            height = 600
            focus = 'center'
          }

          const thumbnailOptions = {
            width: 104,
            height: height,
            direction: 'ttb',
            isNavigation: true,
            fixedWidth: 104,
            gap: 20,
            fixedHeight: 104,
            pagination: false,
            focus: focus,
            breakpoints: {
              768: {
                destroy: true
              }
            }
          }
          this.thumbnailCarousel = new Splide('#thumbnail-carousel', thumbnailOptions)
        }

        const stripAriaRegionSemantics = splide => {
          const apply = root => {
            if (!root) return

            if (root.getAttribute('role') === 'region') {
              root.removeAttribute('role')
            }

            root.removeAttribute('aria-roledescription')
            root.querySelectorAll('[aria-roledescription]').forEach(el => el.removeAttribute('aria-roledescription'))
          }

          const root = splide?.Components?.Elements?.root || splide?.root

          apply(root)
        }

        stripAriaRegionSemantics(this.mainCarousel)
        stripAriaRegionSemantics(this.thumbnailCarousel)

        if (this.isDefault()) {
          this.mainCarousel.sync(this.thumbnailCarousel).mount()
          this.thumbnailCarousel.mount()
          this.mainCarousel.refresh()
        } else {
          this.mainCarousel.mount()
        }

        // Ensure we strip after the forced refresh too.
        stripAriaRegionSemantics(this.mainCarousel)
        stripAriaRegionSemantics(this.thumbnailCarousel)
      }
    }
  )
}

function getVariantSlideIndex(slides, sectionId, variant) {
  const fallbackSlideIndex = 0

  if (!variant.featured_media) return fallbackSlideIndex

  const mediaId = buildMediaId(sectionId, variant)
  const targetIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId)

  return targetIndex < 0 ? fallbackSlideIndex : targetIndex
}

function buildMediaId(sectionId, variant) {
  return `${sectionId}-${variant.featured_media.id}`
}

